import { useEffect, useMemo, useState } from 'react';
import type {
  CreateDashboardCardInput,
  DashboardAggregation,
  DashboardCard,
  DashboardCardConfig,
  DashboardCardEntity,
  DashboardCardType,
  DashboardEntity,
  DashboardEntityCatalog,
  DashboardField,
  DashboardFilter,
  DashboardFilterGroup,
  DashboardFilterMatch,
  DashboardLookup,
  DashboardOperator,
  DashboardThreshold,
  DashboardThresholdOperator,
  DashboardTone,
} from '@catalyst-psa/shared';
import {
  DASHBOARD_AGGREGATIONS,
  DASHBOARD_AGGREGATION_LABELS,
  DASHBOARD_CARD_TYPES,
  DASHBOARD_CARD_TYPE_DESCRIPTIONS,
  DASHBOARD_CARD_TYPE_LABELS,
  DASHBOARD_FILTER_MATCHES,
  DASHBOARD_FILTER_MATCH_JOINERS,
  DASHBOARD_FILTER_MATCH_LABELS,
  DASHBOARD_LABEL_BODY_MAX_LENGTH,
  DASHBOARD_LABEL_ENTITY,
  DASHBOARD_MAX_FILTER_GROUPS,
  DASHBOARD_OPERATORS_BY_TYPE,
  DASHBOARD_OPERATOR_LABELS,
  DASHBOARD_RELATIVE_DATES,
  DASHBOARD_RELATIVE_DATE_LABELS,
  DASHBOARD_STEP_STATUSES,
  DASHBOARD_STEP_STATUS_LABELS,
  DASHBOARD_THRESHOLD_OPERATORS,
  DASHBOARD_TONES,
  DASHBOARD_TONE_LABELS,
  DASHBOARD_VALUELESS_OPERATORS,
  dashboardFilterGroups,
} from '@catalyst-psa/shared';

import { Button } from '../../components/ui/Button.js';
import { Modal } from '../../components/ui/Modal.js';
import { TextAreaField, TextField } from '../../components/ui/Field.js';
import { mutationError } from '../../api/client.js';
import { CustomerSelectField } from '../customers/CustomerSelectField.js';
import { useBillingClassOptions } from '../billing-classes/api.js';
import { useCustomerTypeOptions } from '../customer-types/api.js';
import { useMemberOptions } from '../members/api.js';
import { useServiceCategoryOptions } from '../service-categories/api.js';
import { useAddDashboardCard, useDashboardCatalog, useUpdateDashboardCard } from './api.js';

const CONTROL =
  'rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500';

const LABEL = 'mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500';

interface CardBuilderModalProps {
  isOpen: boolean;
  onClose: () => void;
  dashboardId: string;
  /** The card being edited, or null to add a new one. */
  card: DashboardCard | null;
}

function emptyConfig(): DashboardCardConfig {
  return {
    filters: [],
    filterGroups: [{ match: 'all', filters: [] }],
    filterMatch: 'all',
    aggregation: 'count',
    measureField: null,
    thresholds: [],
    caption: null,
    columns: [],
    sortBy: null,
    sortDir: 'asc',
    groupBy: null,
    dateField: null,
  };
}

/** A simple id/name option list, however the underlying hook shapes its rows. */
interface LookupOption {
  id: string;
  name: string;
}

/** Loads every lookup list once for the whole builder (all are small, cached lists). */
function useLookupOptions(enabled: boolean): Record<DashboardLookup, LookupOption[]> {
  const members = useMemberOptions();
  const serviceCategories = useServiceCategoryOptions();
  const billingClasses = useBillingClassOptions();
  const customerTypes = useCustomerTypeOptions();
  void enabled;

  return useMemo(
    () => ({
      customer: [],
      member: (members.data ?? []).map((m) => ({ id: m.id, name: m.displayName })),
      service_category: (serviceCategories.data ?? []).map((s) => ({ id: s.id, name: s.name })),
      billing_class: (billingClasses.data ?? []).map((b) => ({ id: b.id, name: b.name })),
      ticket_status: [],
      ticket_type: [],
      agreement: [],
      agreement_subtype: [],
      customer_type: (customerTypes.data ?? []).map((c) => ({ id: c.id, name: c.name })),
      product: [],
    }),
    [members.data, serviceCategories.data, billingClasses.data, customerTypes.data],
  );
}

/**
 * Builds or edits one card: what it counts, how it is filtered and how it is displayed.
 * The entity and card type are locked once a card exists — changing either would
 * invalidate every field reference on it, so the user creates a new card instead.
 */
export function CardBuilderModal({
  isOpen,
  onClose,
  dashboardId,
  card,
}: CardBuilderModalProps): JSX.Element {
  const catalogQuery = useDashboardCatalog(isOpen);
  const catalogs = catalogQuery.data ?? [];
  const lookups = useLookupOptions(isOpen);
  const addCard = useAddDashboardCard();
  const updateCard = useUpdateDashboardCard();

  const [title, setTitle] = useState('');
  const [type, setType] = useState<DashboardCardType>('metric');
  const [entity, setEntity] = useState<DashboardCardEntity | ''>('');
  const [config, setConfig] = useState<DashboardCardConfig>(emptyConfig);

  const resetAdd = addCard.reset;
  const resetUpdate = updateCard.reset;

  useEffect(() => {
    if (!isOpen) return;
    resetAdd();
    resetUpdate();
    if (card) {
      setTitle(card.title);
      setType(card.type);
      setEntity(card.entity);
      // The builder always works in groups; a legacy flat card becomes one AND group.
      setConfig({
        ...emptyConfig(),
        ...card.config,
        filters: [],
        filterGroups: dashboardFilterGroups(card.config),
      });
    } else {
      setTitle('');
      setType('metric');
      setEntity('');
      setConfig(emptyConfig());
    }
  }, [isOpen, card, resetAdd, resetUpdate]);

  const catalog: DashboardEntityCatalog | undefined = catalogs.find((c) => c.entity === entity);
  const fields = catalog?.fields ?? [];
  const fieldOf = (key: string | null | undefined): DashboardField | undefined =>
    key ? fields.find((f) => f.key === key) : undefined;

  const filterableFields = fields.filter((f) => f.filterable !== false);
  const columnFields = fields.filter(
    (f) => f.column !== false && f.type !== 'link' && f.type !== 'step',
  );
  const groupableFields = fields.filter((f) => f.groupable);
  const measureFields = fields.filter((f) => f.aggregatable);
  const sortableFields = fields.filter((f) => f.sortable);
  const dateFields = fields.filter((f) => f.type === 'date' || f.type === 'datetime');

  const patch = (changes: Partial<DashboardCardConfig>): void => {
    setConfig((current) => ({ ...current, ...changes }));
  };

  const groups = config.filterGroups ?? [];

  const updateGroup = (
    index: number,
    update: (group: DashboardFilterGroup) => DashboardFilterGroup,
  ): void => {
    setConfig((current) => ({
      ...current,
      filterGroups: (current.filterGroups ?? []).map((group, i) =>
        i === index ? update(group) : group,
      ),
    }));
  };

  const setFilter = (groupIndex: number, index: number, next: DashboardFilter): void => {
    updateGroup(groupIndex, (group) => ({
      ...group,
      filters: group.filters.map((filter, i) => (i === index ? next : filter)),
    }));
  };

  const addFilter = (groupIndex: number): void => {
    const first = filterableFields[0];
    if (!first) return;
    const operator = DASHBOARD_OPERATORS_BY_TYPE[first.type][0] ?? 'eq';
    updateGroup(groupIndex, (group) => ({
      ...group,
      filters: [...group.filters, { field: first.key, operator, value: null, values: [] }],
    }));
  };

  const removeFilter = (groupIndex: number, index: number): void => {
    updateGroup(groupIndex, (group) => ({
      ...group,
      filters: group.filters.filter((_, i) => i !== index),
    }));
  };

  const addGroup = (): void => {
    setConfig((current) => ({
      ...current,
      filterGroups: [...(current.filterGroups ?? []), { match: 'all', filters: [] }],
    }));
  };

  const removeGroup = (index: number): void => {
    setConfig((current) => ({
      ...current,
      filterGroups: (current.filterGroups ?? []).filter((_, i) => i !== index),
    }));
  };

  const toggleColumn = (key: string): void => {
    setConfig((current) => {
      const columns = current.columns ?? [];
      return {
        ...current,
        columns: columns.includes(key)
          ? columns.filter((c) => c !== key)
          : [...columns, key],
      };
    });
  };

  const canSave =
    title.trim().length > 0 &&
    (type === 'label' ||
      (entity !== '' &&
        entity !== DASHBOARD_LABEL_ENTITY &&
        (type !== 'bar' || Boolean(config.groupBy)) &&
        (type !== 'line' || Boolean(config.dateField)) &&
        (config.aggregation === 'count' || Boolean(config.measureField))));

  const handleSubmit = (event: React.FormEvent): void => {
    event.preventDefault();
    if (!canSave) return;
    if (card) {
      updateCard.mutate(
        { dashboardId, cardId: card.id, input: { title: title.trim(), config } },
        { onSuccess: onClose },
      );
      return;
    }
    // Label cards carry the sentinel entity; `canSave` guarantees a real one otherwise.
    const resolvedEntity: DashboardCardEntity | '' =
      type === 'label' ? DASHBOARD_LABEL_ENTITY : entity;
    if (resolvedEntity === '') return;
    const input: CreateDashboardCardInput = {
      title: title.trim(),
      type,
      entity: resolvedEntity,
      config,
    };
    addCard.mutate({ dashboardId, input }, { onSuccess: onClose });
  };

  const error =
    mutationError(addCard.error, 'Could not add the card.') ??
    mutationError(updateCard.error, 'Could not save the card.');
  const isSaving = addCard.isPending || updateCard.isPending;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={card ? 'Edit card' : 'Add card'}
      size="wide"
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button variant="secondary" onClick={onClose} type="button">
            Cancel
          </Button>
          <Button type="submit" form="card-builder" disabled={!canSave} isLoading={isSaving}>
            {card ? 'Save card' : 'Add card'}
          </Button>
        </div>
      }
    >
      <form id="card-builder" onSubmit={handleSubmit} className="space-y-6">
        {error ? (
          <p className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
            {error}
          </p>
        ) : null}

        <div className="grid gap-4 md:grid-cols-2">
          <TextField
            label="Card title"
            required
            value={title}
            maxLength={200}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Overdue projects"
          />
          {type === 'label' ? null : (
            <div>
              <span className={LABEL}>Records</span>
              <select
                className={`${CONTROL} w-full`}
                value={entity}
                disabled={Boolean(card)}
                onChange={(e) => {
                  setEntity(e.target.value as DashboardEntity);
                  setConfig(emptyConfig());
                }}
              >
                <option value="">Choose what to report on…</option>
                {catalogs.map((item) => (
                  <option key={item.entity} value={item.entity}>
                    {item.label}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        <div>
          <span className={LABEL}>Card type</span>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
            {DASHBOARD_CARD_TYPES.map((option) => {
              const selected = type === option;
              return (
                <button
                  key={option}
                  type="button"
                  disabled={Boolean(card)}
                  onClick={() => {
                    setType(option);
                    patch({ groupBy: null, dateField: null });
                  }}
                  className={`rounded-lg border px-3 py-2 text-left ${
                    selected
                      ? 'border-brand-500 bg-brand-50 ring-1 ring-brand-500'
                      : 'border-slate-200 hover:border-slate-300'
                  } ${card ? 'cursor-not-allowed opacity-70' : ''}`}
                >
                  <span className="block text-sm font-medium text-slate-900">
                    {DASHBOARD_CARD_TYPE_LABELS[option]}
                  </span>
                  <span className="mt-0.5 block text-xs text-slate-500">
                    {DASHBOARD_CARD_TYPE_DESCRIPTIONS[option]}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {type === 'label' ? (
          <fieldset className="rounded-lg border border-slate-200 p-4">
            <legend className="px-1 text-sm font-semibold text-slate-900">Body</legend>
            <p className="mb-3 text-xs text-slate-500">
              Optional text shown under the title — instructions for the cards beneath it, for
              example. Leave blank for a title-only bar.
            </p>
            <TextAreaField
              label="Body text"
              rows={4}
              maxLength={DASHBOARD_LABEL_BODY_MAX_LENGTH}
              value={config.body ?? ''}
              onChange={(e) => patch({ body: e.target.value || null })}
            />
          </fieldset>
        ) : entity === '' ? (
          <p className="text-sm text-slate-500">Choose what to report on to continue.</p>
        ) : (
          <>
            {/* ---------------------------------------------------------- filters */}
            <fieldset className="rounded-lg border border-slate-200 p-4">
              <legend className="px-1 text-sm font-semibold text-slate-900">Filters</legend>
              <p className="mb-3 text-xs text-slate-500">
                Conditions inside a group combine with that group&rsquo;s AND/OR. Add a second
                group to build something like (A and B) or (C). Leave a value blank to ignore
                that condition.
              </p>

              <div className="space-y-3">
                {groups.map((group, groupIndex) => (
                  <div key={groupIndex}>
                    {groupIndex > 0 ? (
                      <div className="mb-3 flex items-center gap-2">
                        <span className="h-px flex-1 bg-slate-200" />
                        <select
                          className={`${CONTROL} py-1 text-xs font-semibold`}
                          value={config.filterMatch ?? 'all'}
                          onChange={(e) =>
                            patch({ filterMatch: e.target.value as DashboardFilterMatch })
                          }
                          aria-label="How the groups combine"
                        >
                          {DASHBOARD_FILTER_MATCHES.map((match) => (
                            <option key={match} value={match}>
                              {DASHBOARD_FILTER_MATCH_JOINERS[match]}
                            </option>
                          ))}
                        </select>
                        <span className="h-px flex-1 bg-slate-200" />
                      </div>
                    ) : null}

                    <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3">
                      <div className="mb-2 flex items-center justify-between gap-2">
                        <label className="flex items-center gap-2 text-xs text-slate-600">
                          Match
                          <select
                            className={`${CONTROL} py-1 text-xs`}
                            value={group.match}
                            onChange={(e) =>
                              updateGroup(groupIndex, (current) => ({
                                ...current,
                                match: e.target.value as DashboardFilterMatch,
                              }))
                            }
                          >
                            {DASHBOARD_FILTER_MATCHES.map((match) => (
                              <option key={match} value={match}>
                                {DASHBOARD_FILTER_MATCH_LABELS[match]}
                              </option>
                            ))}
                          </select>
                        </label>
                        {groups.length > 1 ? (
                          <button
                            type="button"
                            onClick={() => removeGroup(groupIndex)}
                            className="rounded px-2 py-1 text-xs text-slate-500 hover:text-rose-600"
                          >
                            Remove group
                          </button>
                        ) : null}
                      </div>

                      <div className="space-y-2">
                        {group.filters.map((filter, index) => {
                          const field = fieldOf(filter.field);
                          const operators = field ? DASHBOARD_OPERATORS_BY_TYPE[field.type] : [];
                          return (
                            <div key={index} className="flex flex-wrap items-start gap-2">
                              <select
                                className={`${CONTROL} min-w-[12rem] flex-1`}
                                value={filter.field}
                                onChange={(e) => {
                                  const next = fields.find((f) => f.key === e.target.value);
                                  if (!next) return;
                                  setFilter(groupIndex, index, {
                                    field: next.key,
                                    operator: DASHBOARD_OPERATORS_BY_TYPE[next.type][0] ?? 'eq',
                                    value: null,
                                    values: [],
                                  });
                                }}
                              >
                                {filterableFields.map((option) => (
                                  <option key={option.key} value={option.key}>
                                    {option.label}
                                  </option>
                                ))}
                              </select>
                              <select
                                className={`${CONTROL} min-w-[10rem]`}
                                value={filter.operator}
                                onChange={(e) =>
                                  setFilter(groupIndex, index, {
                                    ...filter,
                                    operator: e.target.value as DashboardOperator,
                                    value: null,
                                    values: [],
                                  })
                                }
                              >
                                {operators.map((operator) => (
                                  <option key={operator} value={operator}>
                                    {DASHBOARD_OPERATOR_LABELS[operator]}
                                  </option>
                                ))}
                              </select>
                              {field ? (
                                <FilterValueInput
                                  field={field}
                                  filter={filter}
                                  lookups={lookups}
                                  onChange={(next) => setFilter(groupIndex, index, next)}
                                />
                              ) : null}
                              <button
                                type="button"
                                onClick={() => removeFilter(groupIndex, index)}
                                className="rounded px-2 py-2 text-sm text-slate-400 hover:text-rose-600"
                                aria-label="Remove filter"
                              >
                                ✕
                              </button>
                            </div>
                          );
                        })}
                      </div>

                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={() => addFilter(groupIndex)}
                        className="mt-3"
                      >
                        Add condition
                      </Button>
                    </div>
                  </div>
                ))}
              </div>

              {groups.length < DASHBOARD_MAX_FILTER_GROUPS ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={addGroup}
                  className="mt-3"
                >
                  Add group
                </Button>
              ) : null}
            </fieldset>

            {/* ------------------------------------------------------ measurement */}
            {type !== 'list' ? (
              <fieldset className="rounded-lg border border-slate-200 p-4">
                <legend className="px-1 text-sm font-semibold text-slate-900">
                  What to measure
                </legend>
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <span className={LABEL}>Measure</span>
                    <select
                      className={`${CONTROL} w-full`}
                      value={config.aggregation ?? 'count'}
                      onChange={(e) =>
                        patch({
                          aggregation: e.target.value as DashboardAggregation,
                          measureField: e.target.value === 'count' ? null : config.measureField,
                        })
                      }
                    >
                      {DASHBOARD_AGGREGATIONS.map((option) => (
                        <option
                          key={option}
                          value={option}
                          disabled={option !== 'count' && measureFields.length === 0}
                        >
                          {DASHBOARD_AGGREGATION_LABELS[option]}
                        </option>
                      ))}
                    </select>
                  </div>
                  {(config.aggregation ?? 'count') !== 'count' ? (
                    <div>
                      <span className={LABEL}>Field</span>
                      <select
                        className={`${CONTROL} w-full`}
                        value={config.measureField ?? ''}
                        onChange={(e) => patch({ measureField: e.target.value || null })}
                      >
                        <option value="">Choose a field…</option>
                        {measureFields.map((field) => (
                          <option key={field.key} value={field.key}>
                            {field.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  ) : null}
                </div>
              </fieldset>
            ) : null}

            {/* --------------------------------------------------- type specifics */}
            {type === 'metric' ? (
              <MetricOptions config={config} patch={patch} />
            ) : null}

            {type === 'metric' ? (
              <fieldset className="rounded-lg border border-slate-200 p-4">
                <legend className="px-1 text-sm font-semibold text-slate-900">
                  Drill-down columns
                </legend>
                <p className="mb-3 text-xs text-slate-500">
                  Columns shown when you click the number to see the records behind it. Leave
                  all unchecked to use the entity&rsquo;s default columns.
                </p>
                <div className="grid gap-1 sm:grid-cols-2 lg:grid-cols-3">
                  {columnFields.map((field) => (
                    <label key={field.key} className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-slate-300 text-brand-600"
                        checked={(config.columns ?? []).includes(field.key)}
                        onChange={() => toggleColumn(field.key)}
                      />
                      <span className="text-slate-700">{field.label}</span>
                    </label>
                  ))}
                </div>
              </fieldset>
            ) : null}

            {type === 'list' ? (
              <fieldset className="rounded-lg border border-slate-200 p-4">
                <legend className="px-1 text-sm font-semibold text-slate-900">Columns</legend>
                <div className="grid gap-1 sm:grid-cols-2 lg:grid-cols-3">
                  {columnFields.map((field) => (
                    <label key={field.key} className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-slate-300 text-brand-600"
                        checked={(config.columns ?? []).includes(field.key)}
                        onChange={() => toggleColumn(field.key)}
                      />
                      <span className="text-slate-700">{field.label}</span>
                    </label>
                  ))}
                </div>
                <div className="mt-4 grid gap-4 md:grid-cols-3">
                  <div>
                    <span className={LABEL}>Sort by</span>
                    <select
                      className={`${CONTROL} w-full`}
                      value={config.sortBy ?? ''}
                      onChange={(e) => patch({ sortBy: e.target.value || null })}
                    >
                      <option value="">Default order</option>
                      {sortableFields.map((field) => (
                        <option key={field.key} value={field.key}>
                          {field.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <span className={LABEL}>Direction</span>
                    <select
                      className={`${CONTROL} w-full`}
                      value={config.sortDir ?? 'asc'}
                      onChange={(e) => patch({ sortDir: e.target.value as 'asc' | 'desc' })}
                    >
                      <option value="asc">Ascending</option>
                      <option value="desc">Descending</option>
                    </select>
                  </div>
                  <TextField
                    label="Rows to show"
                    type="number"
                    min={1}
                    max={100}
                    value={config.limit ?? 10}
                    onChange={(e) => patch({ limit: Number(e.target.value) || 10 })}
                  />
                </div>
              </fieldset>
            ) : null}

            {type === 'bar' ? (
              <fieldset className="rounded-lg border border-slate-200 p-4">
                <legend className="px-1 text-sm font-semibold text-slate-900">Grouping</legend>
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <span className={LABEL}>Group by</span>
                    <select
                      className={`${CONTROL} w-full`}
                      value={config.groupBy ?? ''}
                      onChange={(e) => patch({ groupBy: e.target.value || null })}
                    >
                      <option value="">Choose a field…</option>
                      {groupableFields.map((field) => (
                        <option key={field.key} value={field.key}>
                          {field.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <TextField
                    label="Bars to show"
                    type="number"
                    min={1}
                    max={25}
                    value={config.groupLimit ?? 8}
                    onChange={(e) => patch({ groupLimit: Number(e.target.value) || 8 })}
                  />
                </div>
              </fieldset>
            ) : null}

            {type === 'line' ? (
              <fieldset className="rounded-lg border border-slate-200 p-4">
                <legend className="px-1 text-sm font-semibold text-slate-900">Trend</legend>
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <span className={LABEL}>Plot by date</span>
                    <select
                      className={`${CONTROL} w-full`}
                      value={config.dateField ?? ''}
                      onChange={(e) => patch({ dateField: e.target.value || null })}
                    >
                      <option value="">Choose a date…</option>
                      {dateFields.map((field) => (
                        <option key={field.key} value={field.key}>
                          {field.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <TextField
                    label="Months to show"
                    type="number"
                    min={1}
                    max={24}
                    value={config.months ?? 12}
                    onChange={(e) => patch({ months: Number(e.target.value) || 12 })}
                  />
                </div>
              </fieldset>
            ) : null}
          </>
        )}
      </form>
    </Modal>
  );
}

/** Metric-only presentation options: caption plus colour thresholds. */
function MetricOptions({
  config,
  patch,
}: {
  config: DashboardCardConfig;
  patch: (changes: Partial<DashboardCardConfig>) => void;
}): JSX.Element {
  const thresholds = config.thresholds ?? [];

  const setThreshold = (index: number, next: DashboardThreshold): void => {
    patch({ thresholds: thresholds.map((t, i) => (i === index ? next : t)) });
  };

  return (
    <fieldset className="rounded-lg border border-slate-200 p-4">
      <legend className="px-1 text-sm font-semibold text-slate-900">Appearance</legend>
      <TextField
        label="Caption (optional)"
        value={config.caption ?? ''}
        maxLength={200}
        onChange={(e) => patch({ caption: e.target.value || null })}
        placeholder="Shown under the number"
      />
      <p className="mb-2 mt-4 text-xs text-slate-500">
        Colour rules — the first rule that matches wins.
      </p>
      <div className="space-y-2">
        {thresholds.map((threshold, index) => (
          <div key={index} className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-slate-600">When the number is</span>
            <select
              className={CONTROL}
              value={threshold.operator}
              onChange={(e) =>
                setThreshold(index, {
                  ...threshold,
                  operator: e.target.value as DashboardThresholdOperator,
                })
              }
            >
              {DASHBOARD_THRESHOLD_OPERATORS.map((operator) => (
                <option key={operator} value={operator}>
                  {THRESHOLD_LABELS[operator]}
                </option>
              ))}
            </select>
            <input
              className={`${CONTROL} w-24`}
              value={threshold.value}
              onChange={(e) => setThreshold(index, { ...threshold, value: e.target.value })}
              placeholder="0"
            />
            <span className="text-sm text-slate-600">show</span>
            <select
              className={CONTROL}
              value={threshold.tone}
              onChange={(e) =>
                setThreshold(index, { ...threshold, tone: e.target.value as DashboardTone })
              }
            >
              {DASHBOARD_TONES.map((tone) => (
                <option key={tone} value={tone}>
                  {DASHBOARD_TONE_LABELS[tone]}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => patch({ thresholds: thresholds.filter((_, i) => i !== index) })}
              className="rounded px-2 py-2 text-sm text-slate-400 hover:text-rose-600"
              aria-label="Remove colour rule"
            >
              ✕
            </button>
          </div>
        ))}
      </div>
      {thresholds.length < 5 ? (
        <Button
          type="button"
          variant="secondary"
          size="sm"
          className="mt-3"
          onClick={() =>
            patch({ thresholds: [...thresholds, { operator: 'gte', value: '1', tone: 'amber' }] })
          }
        >
          Add colour rule
        </Button>
      ) : null}
    </fieldset>
  );
}

const THRESHOLD_LABELS: Record<DashboardThresholdOperator, string> = {
  gte: 'at least',
  gt: 'more than',
  lte: 'at most',
  lt: 'less than',
  eq: 'exactly',
};

/** Renders the right value control for a filter's field type and operator. */
function FilterValueInput({
  field,
  filter,
  lookups,
  onChange,
}: {
  field: DashboardField;
  filter: DashboardFilter;
  lookups: Record<DashboardLookup, LookupOption[]>;
  onChange: (next: DashboardFilter) => void;
}): JSX.Element | null {
  if (DASHBOARD_VALUELESS_OPERATORS.includes(filter.operator)) return null;
  const set = (value: string): void => onChange({ ...filter, value });

  if (field.type === 'step') {
    return (
      <select
        className={`${CONTROL} min-w-[10rem]`}
        value={filter.value ?? ''}
        onChange={(e) => set(e.target.value)}
      >
        <option value="">Choose…</option>
        {DASHBOARD_STEP_STATUSES.map((status) => (
          <option key={status} value={status}>
            {DASHBOARD_STEP_STATUS_LABELS[status]}
          </option>
        ))}
      </select>
    );
  }

  if (field.type === 'enum') {
    return (
      <select
        className={`${CONTROL} min-w-[10rem]`}
        value={filter.value ?? ''}
        onChange={(e) => set(e.target.value)}
      >
        <option value="">Choose…</option>
        {(field.options ?? []).map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    );
  }

  if (field.type === 'uuid') {
    if (field.lookup === 'customer') {
      return (
        <div className="min-w-[16rem]">
          <CustomerSelectField
            value={filter.value ?? ''}
            onChange={(id) => set(id)}
            placeholder="Search customers…"
            allowClear
          />
        </div>
      );
    }
    const options = field.lookup ? (lookups[field.lookup] ?? []) : [];
    return (
      <select
        className={`${CONTROL} min-w-[12rem]`}
        value={filter.value ?? ''}
        onChange={(e) => set(e.target.value)}
      >
        <option value="">Choose…</option>
        {options.map((option) => (
          <option key={option.id} value={option.id}>
            {option.name}
          </option>
        ))}
      </select>
    );
  }

  if (field.type === 'date' || field.type === 'datetime') {
    if (filter.operator === 'relative') {
      return (
        <select
          className={`${CONTROL} min-w-[12rem]`}
          value={filter.value ?? ''}
          onChange={(e) => set(e.target.value)}
        >
          <option value="">Choose…</option>
          {DASHBOARD_RELATIVE_DATES.map((token) => (
            <option key={token} value={token}>
              {DASHBOARD_RELATIVE_DATE_LABELS[token]}
            </option>
          ))}
        </select>
      );
    }
    return (
      <div className="flex items-center gap-2">
        <input
          type="date"
          className={CONTROL}
          value={filter.value ?? ''}
          onChange={(e) => set(e.target.value)}
        />
        {filter.operator === 'between' ? (
          <input
            type="date"
            className={CONTROL}
            value={filter.valueTo ?? ''}
            onChange={(e) => onChange({ ...filter, valueTo: e.target.value })}
          />
        ) : null}
      </div>
    );
  }

  const numeric = field.type === 'number' || field.type === 'money' || field.type === 'hours';
  return (
    <div className="flex items-center gap-2">
      <input
        type={numeric ? 'number' : 'text'}
        className={`${CONTROL} min-w-[10rem]`}
        value={filter.value ?? ''}
        onChange={(e) => set(e.target.value)}
        placeholder="Value"
      />
      {filter.operator === 'between' ? (
        <input
          type="number"
          className={`${CONTROL} w-28`}
          value={filter.valueTo ?? ''}
          onChange={(e) => onChange({ ...filter, valueTo: e.target.value })}
          placeholder="and"
        />
      ) : null}
    </div>
  );
}
